import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { AiFillEye, AiFillEyeInvisible, AiOutlineDelete } from "react-icons/ai";
import { ReactComponent as ArrowBackIcon } from "assets/images/common/svgs/arrow_back.svg";
import { useNavigate } from "react-router-dom";
import { ReactComponent as EditProfileIcon } from "assets/images/common/svgs/EditProfileIcon.svg";
import editProfileUSer from "assets/images/common/others/editProfileUSer.png";

type FormValues = {
  profileImage: FileList | null;
  hostName: string;
  businessName: string;
  industry: string;
  address: string;
  businessNumber: string;
  businessEmail: string;
  password: string;
};

const CreateHost: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>();

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageDelete = () => {
    setProfileImage(null);
    clearErrors("profileImage");
  };

  const onSubmit = (data: FormValues) => {
    console.log("Form Data: ", data);
  };

  const goToBack = () => {
    navigate(-1);
  };

  return (
    <div className="">
      <h1 className="text-base text-primaryText font-bold mb-6 flex items-center gap-3">
        <span
          onClick={goToBack}
          className="bg-transparentBlueBg w-[44px] h-[44px] rounded-full flex items-center justify-center cursor-pointer"
        >
          <ArrowBackIcon />
        </span>
        Create a Host
      </h1>

      <div className="w-full bg-white p-4 rounded-[8px]">
        <h1 className="tex-xl text-primaryText font-bold mb-6">
          Personal Details
        </h1>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full flex flex-col gap-3 mt-3"
        >
          <div>
            <div className="flex">
              <div className="relative">
                <img
                  src={editProfileUSer}
                  alt="Profile"
                  className="w-36 h-36 rounded-full object-cover"
                />
                {profileImage ? (
                  <button
                    onClick={handleImageDelete}
                    className="absolute bottom-0 right-0 bg-red-500 text-white p-2 rounded-full cursor-pointer"
                  >
                    <AiOutlineDelete />
                  </button>
                ) : (
                  <label
                    htmlFor="profileImage"
                    className="absolute bottom-0 right-0  cursor-pointer"
                  >
                    <EditProfileIcon />
                  </label>
                )}
                <input
                  id="profileImage"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  {...register("profileImage", {
                    required: "Profile image is required",
                  })}
                  onChange={handleImageChange}
                />
              </div>
            </div>
            {errors.profileImage && (
              <p className="text-red-500 text-sm mt-2">
                {errors.profileImage.message}
              </p>
            )}
            <p className="font-medium text-base text-labelText mt-2">
              Upload Profile Pic
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {/* Host Name */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">Host Name</label>
              <input
                type="text"
                placeholder="Enter Host Name"
                {...register("hostName", { required: "Host Name is required" })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
              />
              {errors.hostName && (
                <span className="text-red-500 text-sm">
                  {errors.hostName.message}
                </span>
              )}
            </div>

            {/* Business Name */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">Business Name</label>
              <input
                type="text"
                placeholder="Enter Business Name"
                {...register("businessName", {
                  required: "Business Name is required",
                })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
              />
              {errors.businessName && (
                <span className="text-red-500 text-sm">
                  {errors.businessName.message}
                </span>
              )}
            </div>

            {/* Industry */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">Industry</label>
              <select
                {...register("industry", { required: "Industry is required" })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none bg-white"
              >
                <option value="">Select</option>
                <option value="IT">IT</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Education">Education</option>
              </select>
              {errors.industry && (
                <span className="text-red-500 text-sm">
                  {errors.industry.message}
                </span>
              )}
            </div>

            {/* Address */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">Address</label>
              <input
                type="text"
                placeholder="Enter Address"
                {...register("address", { required: "Address is required" })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
              />
              {errors.address && (
                <span className="text-red-500 text-sm">
                  {errors.address.message}
                </span>
              )}
            </div>

            {/* Business Number */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">Business Number</label>
              <input
                type="text"
                placeholder="Enter Business Number"
                {...register("businessNumber", {
                  required: "Business Number is required",
                })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
              />
              {errors.businessNumber && (
                <span className="text-red-500 text-sm">
                  {errors.businessNumber.message}
                </span>
              )}
            </div>

            {/* Business Email */}
            <div className="flex flex-col gap-2">
              <label className="text-labelText">Business Email</label>
              <input
                type="email"
                placeholder="Enter Business Email"
                {...register("businessEmail", {
                  required: "Business Email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Invalid email format",
                  },
                })}
                className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
              />
              {errors.businessEmail && (
                <span className="text-red-500 text-sm">
                  {errors.businessEmail.message}
                </span>
              )}
            </div>

            {/* Password Input */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label className="text-labelText text-lg">Password</label>
                <div
                  className="cursor-pointer flex items-center text-[#606060]"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <>
                      <AiFillEyeInvisible size={18} className="mr-1" />
                      <span>Hide</span>
                    </>
                  ) : (
                    <>
                      <AiFillEye size={18} className="mr-1" />
                      <span>Show</span>
                    </>
                  )}
                </div>
              </div>
              <div className="">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters long",
                    },
                  })}
                  className="w-full h-[51px] px-4 border border-gray-300 rounded-lg outline-none"
                />
              </div>
              <div className="flex justify-between">
                {errors.password && (
                  <span className="text-red-500 text-sm">
                    {errors.password.message}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-start mt-3">
            <button
              type="submit"
              className="w-[125px] h-[45px]  text-xl font-semibold text-white bg-darkTealBlue rounded-[8px]"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateHost;
